import { compact, isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import { SelectChangeEvent } from "@mui/material";

import { getTimezoneName } from "@app/booking/utils";

import Dropdown, { DropdownMenuItem, DropdownProps } from "@rui/Dropdown";

interface TimezoneSelectorProps
  extends Omit<DropdownProps, "onChange" | "name"> {
  disableDefaultTimezone?: boolean;
  onChange: (timezone: string) => void;
  selected?: string | null;
}

const LOCAL_DATE = DateTime.local();
const DEFAULT_TIMEZONE = LOCAL_DATE.zone.name;

const getTimezones = () => {
  try {
    return (Intl as any).supportedValuesOf("timeZone");
  } catch (error) {
    // this is a native JS function, but some browsers/clients may not support it.
    // in the event that's the case, return an empty list of zones (selector will
    // not render at all in that case)
    return [];
  }
};

const TIMEZONES: string[] = getTimezones();
const DROPDOWN_ITEMS = compact(TIMEZONES).map((zone) => {
  return {
    value: zone,
    label: getTimezoneName(zone),
    offset: parseInt(DateTime.local({ zone }).toFormat("Z")),
  };
});

const TimezoneSelector = ({
  disableDefaultTimezone = false,
  onChange,
  selected,
  ...dropdownProps
}: TimezoneSelectorProps) => {
  const selectedValue =
    selected ?? (disableDefaultTimezone ? null : DEFAULT_TIMEZONE);

  if (isEmpty(DROPDOWN_ITEMS) || (!selectedValue && !disableDefaultTimezone)) {
    return null;
  }

  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value);
  };

  return (
    <Dropdown
      error={!selectedValue}
      {...dropdownProps}
      name="timezone-selector"
      onChange={handleChange}
      placeholder="Unspecified"
      selectedItem={
        selectedValue
          ? {
              value: selectedValue,
              label: getTimezoneName(selectedValue),
            }
          : undefined
      }
    >
      {DROPDOWN_ITEMS.map(({ value, label }) => (
        <DropdownMenuItem key={value} value={value} label={label} />
      ))}
    </Dropdown>
  );
};

export default TimezoneSelector;
